import { useEffect, useRef, useState } from 'react'

/**
 * La cantidad de un renglón, que se pueda escribir encima.
 *
 * EL CAMPO SE DEFENDÍA DE QUIEN ESCRIBÍA. Nacía en 1 —razonable— pero cada
 * tecla pasaba por `Math.max(1, …)`, así que al borrar el 1 para poner «35» el
 * campo volvía a ponerlo solo. En el teléfono, donde no hay clic derecho ni
 * doble clic cómodo, la única salida era pelear con el cursor. Johan lo contó
 * tal cual: «tienen que borrar ese 1 para volver a escribir el número que es de
 * verdad, y no se permite borrarlo fácilmente».
 *
 * Dos cambios y se acabó:
 *
 *  · AL TOCARLO SE SELECCIONA TODO. La primera tecla reemplaza lo que había,
 *    que es lo que uno espera de un campo con un valor por defecto.
 *  · SE PUEDE DEJAR VACÍO MIENTRAS SE ESCRIBE. El mínimo se aplica al salir,
 *    no en cada tecla: si se va en blanco, vuelve a 1.
 *
 * El valor de fuera sigue siendo un número; lo que vive aquí es el texto a
 * medio escribir, que es justo lo que el componente de antes no admitía.
 */
export function CampoDeCantidad({
  valor,
  onCambio,
  className,
  title,
  ...resto
}: {
  valor: number
  onCambio: (valor: number) => void
  className?: string
  title?: string
  'aria-label': string
}) {
  const [texto, setTexto] = useState(String(valor))
  const escribiendo = useRef(false)

  /*
   * MIENTRAS EL DEDO ESTÁ EN EL CAMPO, MANDA EL CAMPO.
   *
   * La primera versión sincronizaba siempre que el valor de fuera no cuadrara
   * con el texto, y eso borraba lo que se estaba escribiendo: al vaciar el
   * campo el texto quedaba en «» y el valor seguía en 35, no cuadraban, y el
   * efecto reponía «35» encima. Lo destapó la prueba —vaciar no vaciaba— y es
   * exactamente la pelea con el cursor que se quería quitar.
   *
   * Se sincroniza solo cuando NADIE lo está editando: al reabrir el borrador o
   * al cargar lo guardado, que es para lo que hacía falta.
   */
  useEffect(() => {
    if (escribiendo.current) return
    setTexto(String(valor))
  }, [valor])

  return (
    <input
      className={className}
      type="text"
      inputMode="decimal"
      value={texto}
      onFocus={(e) => {
        escribiendo.current = true
        e.currentTarget.select()
      }}
      onChange={(e) => {
        // Dígitos y un separador decimal: hay materiales que van en 2,5 m.
        const limpio = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.')
        setTexto(limpio)
        const n = Number(limpio)
        if (limpio !== '' && Number.isFinite(n) && n > 0) onCambio(n)
      }}
      onBlur={() => {
        escribiendo.current = false
        const n = Number(texto)
        if (texto === '' || !Number.isFinite(n) || n <= 0) {
          setTexto('1')
          onCambio(1)
        } else {
          setTexto(String(n))
        }
      }}
      title={title}
      {...resto}
    />
  )
}
